/**
 * Serviço para API de Cards de Projetos e Demandas
 * Endpoints específicos para o dashboard do Gestor
 * 
 * @author Equipe GestaoeCRM
 * @date 03/10/2025
 * @updated 08/10/2025 - Adicionado suporte para demandas sem projeto
 */

import api from './api';

// Card de Projeto
export interface ProjectCard {
  id: string;
  type: 'project';
  name: string;
  description: string | null;
  status: string;
  client: {
    id: string;
    name: string;
  };
  manager: {
    id: string;
    name: string;
    email: string;
  } | null;
  activeOSCount: number;
  runningOSCount: number;  // ✅ NOVO: OS com timer rodando
  totalOSCount: number;
  createdAt: string;
  updatedAt: string;
}

// Card de Demanda (sem projeto vinculado)
export interface DemandCard {
  id: string;
  type: 'demand';
  name: string;
  description: string | null;
  status: string;
  priority: string;
  assignee: {
    id: string;
    name: string;
    email: string;
  } | null;
  manager: {
    id: string;
    name: string;
    email: string;
  } | null;
  activeOSCount: number;
  runningOSCount: number;  // ✅ NOVO: OS com timer rodando
  totalOSCount: number;
  createdAt: string;
  updatedAt: string;
}

// Union type para qualquer tipo de card
export type CardItem = ProjectCard | DemandCard;

export interface ProjectCardsResponse {
  success: boolean;
  data: ProjectCard[];
  total: number;
}

export interface ProjectsAndDemandsCardsResponse {
  success: boolean;
  data: CardItem[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export const projectCardsApi = {
  /**
   * Busca projetos e demandas sem projeto com contagem de jobs/tasks ativos
   * NOVO: Endpoint unificado para dashboard do Gestor
   */
  getProjectsAndDemandsCards: async (params?: {
    search?: string;
    status?: string;
    page?: number;
    limit?: number;
  }): Promise<ProjectsAndDemandsCardsResponse> => {
    const response = await api.get('/projects/cards-with-demands', { params });
    return response.data;
  },

  /**
   * Busca projetos com contagem de jobs/tasks ativos para o dashboard do Gestor
   * LEGADO: Mantido para compatibilidade
   */
  getProjectCards: async (params?: {
    search?: string;
    status?: string;
    page?: number;
    limit?: number;
  }): Promise<ProjectCardsResponse> => {
    const response = await api.get('/projects/cards', { params });
    return response.data;
  },

  /**
   * Busca OS ativas de um projeto específico
   */
  getProjectActiveOS: async (projectId: string, params?: {
    search?: string;
    page?: number;
    limit?: number;
  }) => {
    const response = await api.get(`/projects/${projectId}/active-os`, { params });
    return response.data;
  },

  /**
   * Busca OS ativas de uma demanda específica
   * NOVO: Para demandas sem projeto vinculado
   */
  getDemandActiveOS: async (demandId: string, params?: {
    search?: string;
    page?: number;
    limit?: number;
  }) => {
    const response = await api.get(`/demands/${demandId}/active-os`, { params });
    return response.data;
  }
};
